import Swup from 'swup';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import SwupScrollPlugin from '@swup/scroll-plugin';
// @ts-expect-error Package doesn't have types
import SwupGaPlugin from '@swup/ga-plugin';
// @ts-expect-error Package doesn't have types
import SwupGtmPlugin from '@swup/gtm-plugin';
import SwupScriptsPlugin from '@swup/scripts-plugin';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupProgressPlugin from '@swup/progress-plugin';
import { isFunction, type defineModule } from '../utils/helpers';
import { HASH_TO_MODAL_ID_MAP } from '../components/modals/anchors';

// Modules
import aosModule from './aos';
import complianzModule from './complianz';

const autoImportedModules = import.meta.glob<ReturnType<typeof defineModule>>(
  ['@/../Templates/blocks/*/script.ts', '../components/**/*.ts'],
  {
    eager: true,
    import: 'default',
  },
);

const modules: ReturnType<typeof defineModule>[] = [
  aosModule,
  complianzModule,
  ...Object.values(autoImportedModules),
];

const setup = () => Promise.all(modules.map((module) => module.setup()));
const cleanup = () =>
  Promise.all(
    modules.map((module) => isFunction(module.cleanup) && module.cleanup()),
  );

const swupNot = '[data-no-swup],[href*="/wp/"],[href*="/app/uploads/"]';

/**
 * Distance from the top of the viewport to below the sticky menu bar, admin bar
 * included — the bar is stuck to the top on every scroll position, so its
 * `bottom` is exactly the space an anchor has to clear.
 *
 * `html` carries a `scroll-padding-top` for this, but that only governs scrolls
 * the browser performs itself (focus, a hash on page load). The scroll plugin
 * animates with its own JS, so it needs the offset handed to it or every in-page
 * anchor lands underneath the bar. Both paths honour a target's `scroll-margin`,
 * so a block can still add breathing room of its own on top of this.
 */
const getStickyOffset = () =>
  document.querySelector<HTMLElement>('.menu__bar')?.getBoundingClientRect()
    .bottom ?? 0;

// Getters, not plain values: the visitor can flip the OS setting without reloading.
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const animateScroll = {
  betweenPages: false,
  get samePage() {
    return !reducedMotion.matches;
  },
  get samePageWithHash() {
    return !reducedMotion.matches;
  },
};

export const swup = new Swup({
  animationSelector: '[class*="swup-transition-"]',
  containers: ['.page-wrapper'],
  plugins: [
    new SwupPreloadPlugin(),
    new SwupBodyClassPlugin(),
    new SwupScrollPlugin({
      animateScroll,
      offset: getStickyOffset,
    }),
    new SwupGaPlugin({ gaMeasurementId: window.gaTrackingId }),
    new SwupGtmPlugin(),
    new SwupScriptsPlugin({
      head: false,
      body: true,
    }),
    new SwupHeadPlugin({
      persistTags: import.meta.env.DEV ? 'style[data-vite-dev-id]' : false,
    }),
    new SwupProgressPlugin(),
  ],
  ignoreVisit: (url, { el }) => {
    if (!(el instanceof HTMLAnchorElement)) return false;

    return Object.keys(HASH_TO_MODAL_ID_MAP).includes(
      new URL(el.href).hash.substring(1),
    );
  },
  linkSelector: `a[href^="${window.location.origin}"]:not(${swupNot}), a[href^="/"]:not(${swupNot}), a[href^="#"]:not(${swupNot})`,
});

swup.hooks.on('visit:end', async () => {
  await setup();
});

swup.hooks.on('visit:start', async () => {
  await cleanup();
});

setup();
